import styled from "@emotion/styled";
import { DevTool } from "@hookform/devtools";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Modal, Stack } from "@mui/material";
import {
  Button,
  PhoneInputField,
  Radio,
  Select,
  TextField,
  Upload
} from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import { MutationError } from "components/UI/MutationError";
import { formatISO } from "date-fns";
import { useChurchStore } from "features/churches";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreatePartner } from "../api";
import { useMembersStore } from "../stores";
import { createPartnerSchema } from "../validation";

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: auto;
  gap: 24px;
  align-items: flex-end;
`;

export function AddPartnerModal() {
  const addPartnerModalOpen = useMembersStore(
    state => state.addPartnerModalOpen
  );
  const closeAddPartnerModal = useMembersStore(
    state => state.closeAddPartnerModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const partnerMutation = useCreatePartner();
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, defaultValues },
    reset,
    watch
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      gender: "",
      birth_date: null,
      phone: "",
      email: "",
      address: "",
      partner_type: "",
      organization_name: "",
      image: null
    },
    resolver: yupResolver(createPartnerSchema)
  });

  const partnerType = watch("partner_type");

  useEffect(() => {
    if (partnerMutation.isSuccess) {
      reset({ ...defaultValues });
    }
  }, [partnerMutation.isSuccess]);

  const onSubmit = data => {
    const partnerData = {
      first_name: data.first_name,
      last_name: data.last_name,
      gender: data.gender,
      phone: data.phone,
      email: data.email,
      address: data.address,
      partner_type: data.partner_type
    };

    if (data.partner_type === "organization" && data.organization_name) {
      partnerData.organization_name = data.organization_name;
    }

    if (data.birth_date) {
      partnerData.birth_date = formatISO(data.birth_date, {
        representation: "date"
      });
    }

    if (data.image) {
      partnerData["image"] = data.image;
    }

    const formData = new FormData();

    for (let key in partnerData) {
      formData.append(key, partnerData[key]);
    }

    partnerMutation.mutate({
      churchId: selectedChurch?.id,
      partnerData: formData
    });
  };

  return (
    <>
      <DevTool control={control} />
      <Modal open={addPartnerModalOpen} onClose={closeAddPartnerModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            borderRadius: 2,
            width: { xs: "95vw", md: "880px" },
            maxHeight: "90vh",
            overflowX: "hidden",
            overflowY: "auto"
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: "primary.main",
              color: "primary.contrastText"
            }}
          >
            {t("members.partners.AddPartner")}
          </Box>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              py: 4,
              px: 8,
              backgroundColor: "white"
            }}
            spacing={2}
          >
            <FormGrid>
              <Select
                label={t("members.partners.PartnerType")}
                name="partner_type"
                options={[
                  {
                    optionLabel: t("members.partners.Individual"),
                    value: "individual"
                  },
                  {
                    optionLabel: t("members.partners.Organization"),
                    value: "organization"
                  }
                ]}
                register={register}
                error={!!errors.partner_type?.message}
                helperText={errors.partner_type?.message}
                required
                placeholder={t("members.partners.SelectPartnerType")}
              />
              {partnerType === "organization" && (
                <Box sx={{ gridColumn: "span 2" }}>
                  <TextField
                    label={t("members.partners.OrganizationName")}
                    name="organization_name"
                    error={!!errors.organization_name?.message}
                    helperText={errors.organization_name?.message}
                    register={register}
                    required
                  />
                </Box>
              )}
              <TextField
                label={`${
                  partnerType === "individual"
                    ? t("form.common.firstName")
                    : t("members.partners.ContactPersonFirstName")
                }`}
                name="first_name"
                error={!!errors.first_name?.message}
                helperText={errors.first_name?.message}
                register={register}
                required={partnerType === "individual"}
              />
              <TextField
                label={`${
                  partnerType === "individual"
                    ? t("form.common.fatherName")
                    : t("members.partners.ContactPersonLastName")
                }`}
                name="last_name"
                error={!!errors.last_name?.message}
                helperText={errors.last_name?.message}
                register={register}
                required={partnerType === "individual"}
              />
              {partnerType === "individual" && (
                <Radio
                  label={t("form.common.gender")}
                  name="gender"
                  buttons={[
                    {
                      radioLabel: t("form.common.male"),
                      value: "male",
                      id: "male"
                    },
                    {
                      radioLabel: t("form.common.female"),
                      value: "female",
                      id: "female"
                    }
                  ]}
                  row={true}
                  register={register}
                  error={!!errors.gender?.message}
                  helperText={errors.gender?.message}
                  required
                />
              )}
              {partnerType === "individual" && (
                <Box sx={{ gridColumn: "span 2" }}>
                  <Controller
                    name="birth_date"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label={t("form.common.dateOfBirth")}
                        name="birth_date"
                        error={!!errors.birth_date?.message}
                        helperText={errors.birth_date?.message}
                        {...field}
                        disableFutureDates
                      />
                    )}
                  />
                </Box>
              )}
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInputField
                    label={`${
                      partnerType === "individual"
                        ? t("form.common.phone")
                        : t("members.partners.OrganizationPhone")
                    }`}
                    variant="filled"
                    error={!!errors.phone?.message}
                    helperText={errors.phone?.message}
                    {...field}
                    required
                  />
                )}
              />
              <TextField
                label={`${
                  partnerType === "individual"
                    ? t("form.common.email")
                    : t("members.partners.OrganizationEmail")
                }`}
                name="email"
                error={!!errors.email?.message}
                helperText={errors.email?.message}
                register={register}
              />
              <TextField
                label={`${
                  partnerType === "individual"
                    ? t("form.common.address")
                    : t("members.partners.OrganizationAddress")
                }`}
                name="address"
                error={!!errors.address?.message}
                helperText={errors.address?.message}
                register={register}
              />
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <Box sx={{ gridColumn: "span 3" }}>
                    <Upload
                      title={`${
                        partnerType === "individual"
                          ? t("addMembers.step1.UploadPhoto")
                          : t("members.partners.OrganizationLogo")
                      }`}
                      buttonTitle={t("ui.dropzone.browse")}
                      error={!!errors.image?.message}
                      helperText={errors.image?.message}
                      onChange={field.onChange}
                    />
                  </Box>
                )}
              />
            </FormGrid>
            <MutationError error={partnerMutation.error} sx={{ mt: 2 }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={2}
              sx={{ pt: 4 }}
            >
              <Button
                size="large"
                variant="contained"
                disabled={partnerMutation.isLoading}
                color="secondary"
                flavor="secondary"
                onClick={() => {
                  reset();
                  closeAddPartnerModal();
                }}
              >
                {t("form.common.cancel")}
              </Button>
              <Button
                type="submit"
                size="large"
                variant="contained"
                disabled={partnerMutation.isLoading}
              >
                {t("form.common.submit")}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
